import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './useAuth';
import { getMyOrganizationsApi } from '../api/apiServices';

// Which organization's data the admin area is currently scoped to.
// `currentOrgId === null` means "Personal" -- the user's own courses (plus
// anything they can edit via an org membership), matching what
// GET /courses/mine already returns with no organizationId filter. Picking
// one of `organizations` narrows the Dashboard and Courses views down to
// just that org, via the same `organizationId` query param the backend
// already accepts on /courses/mine and /courses/instructor/analytics.
const OrgContext = createContext(undefined);

const storageKey = (userId) => `learnhub_admin_org_${userId || 'anon'}`;

export const OrgProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const userId = user?.userId;

  const [organizations, setOrganizations] = useState([]);
  const [currentOrgId, setCurrentOrgIdState] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadOrganizations = useCallback(async () => {
    if (!isAuthenticated) {
      setOrganizations([]);
      setCurrentOrgIdState(null);
      return;
    }

    try {
      setLoading(true);
      const res = await getMyOrganizationsApi();
      const orgs = res?.organizations || [];
      setOrganizations(orgs);

      let saved = null;
      try {
        saved = localStorage.getItem(storageKey(userId));
      } catch {
        // private browsing / storage disabled -- fall back to "Personal"
      }
      setCurrentOrgIdState(saved && orgs.some((o) => o._id === saved) ? saved : null);
    } catch (err) {
      console.error('Failed to load organizations for the org switcher:', err);
      setOrganizations([]);
      setCurrentOrgIdState(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, userId]);

  useEffect(() => {
    loadOrganizations();
  }, [loadOrganizations]);

  const setCurrentOrgId = (id) => {
    setCurrentOrgIdState(id || null);
    try {
      if (id) {
        localStorage.setItem(storageKey(userId), id);
      } else {
        localStorage.removeItem(storageKey(userId));
      }
    } catch {
      // ignore storage errors
    }
  };

  const currentOrg = organizations.find((o) => o._id === currentOrgId) || null;

  return (
    <OrgContext.Provider
      value={{
        organizations,
        currentOrgId,
        currentOrg,
        setCurrentOrgId,
        loading,
        refreshOrganizations: loadOrganizations,
      }}
    >
      {children}
    </OrgContext.Provider>
  );
};

export const useOrg = () => {
  const ctx = useContext(OrgContext);
  if (ctx === undefined) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return ctx;
};
